import {
  CalendarDays,
  Check,
  ChevronDown,
  ExternalLink,
  FileText,
  Pencil,
  Plus,
  Search,
  Trash2,
  X
} from "lucide-react";
import { useMemo, useState } from "react";
import type { ActivityEntry, Note } from "../types";

/** Lowercase and strip accents so "reunion" finds "Réunion". */
function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("fr-FR");
}

const dayFormat = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit"
});

/** "modifié" only when it actually was, so the label carries information. */
function noteDate(note: Note): { label: string; iso: string } {
  const updated = note.updatedAt && note.updatedAt !== note.createdAt ? note.updatedAt : undefined;
  const iso = updated || note.createdAt;
  const stamp = new Date(iso);
  if (Number.isNaN(stamp.getTime())) return { label: "", iso };
  return { label: `${updated ? "Modifiée" : "Créée"} le ${dayFormat.format(stamp)}`, iso };
}

function NoteCard({
  note,
  expanded,
  onToggle,
  onSave,
  onDelete
}: {
  note: Note;
  expanded: boolean;
  onToggle: () => void;
  onSave: (title: string, body: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { label, iso } = noteDate(note);

  const startEditing = () => {
    setTitle(note.title);
    setBody(note.body);
    setError("");
    setEditing(true);
  };

  const save = async () => {
    const cleanTitle = title.trim();
    const cleanBody = body.trim();
    if (!cleanTitle || !cleanBody) {
      setError("Un titre et un contenu sont nécessaires.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(cleanTitle, cleanBody);
      setEditing(false);
    } catch {
      setError("La note n’a pas pu être enregistrée. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  // Editing happens inside the card. A composer at the top of the page moved
  // the note out of view and lost the place in a long list.
  if (editing) {
    return (
      <li className="note-card is-editing">
        <input
          className="note-card-title-input"
          value={title}
          maxLength={160}
          aria-label="Titre de la note"
          onChange={(event) => setTitle(event.target.value)}
        />
        <textarea
          value={body}
          maxLength={50_000}
          rows={Math.min(18, Math.max(6, body.split("\n").length + 1))}
          aria-label="Contenu de la note"
          onChange={(event) => setBody(event.target.value)}
        />
        {error && <p className="inline-error" role="alert">{error}</p>}
        <div className="note-card-actions">
          <button className="button ghost compact" type="button" onClick={() => setEditing(false)}>
            Annuler
          </button>
          <button
            className="button primary compact"
            type="button"
            disabled={saving}
            onClick={() => void save()}
          >
            <Check size={16} aria-hidden="true" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={`note-card ${expanded ? "is-open" : ""}`}>
      <button
        className="note-card-head"
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="note-card-heading">
          <strong>{note.title}</strong>
          <time dateTime={iso}>{label}</time>
        </span>
        <ChevronDown size={18} aria-hidden="true" />
      </button>

      {/* Collapsed shows a preview; expanded shows the note in full, without
          having to open an editor just to read it. */}
      <p className={expanded ? "note-card-body" : "note-card-body is-clamped"}>{note.body}</p>

      {expanded && (
        <div className="note-card-actions">
          {confirmingDelete ? (
            <>
              <span className="note-card-confirm">Supprimer définitivement&nbsp;?</span>
              <button
                className="button ghost compact"
                type="button"
                onClick={() => setConfirmingDelete(false)}
              >
                Annuler
              </button>
              <button
                className="button danger compact"
                type="button"
                onClick={() => void onDelete()}
              >
                Supprimer
              </button>
            </>
          ) : (
            <>
              <button className="button ghost compact" type="button" onClick={startEditing}>
                <Pencil size={16} aria-hidden="true" />
                Modifier
              </button>
              <button
                className="button ghost compact danger-text"
                type="button"
                onClick={() => setConfirmingDelete(true)}
                aria-label={`Supprimer ${note.title}`}
              >
                <Trash2 size={16} aria-hidden="true" />
                Supprimer
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function NotesView({
  notes,
  activity,
  loading,
  onCapture,
  onCreate,
  onUpdate,
  onDelete
}: {
  notes: Note[];
  activity: ActivityEntry[];
  loading: boolean;
  onCapture: () => void;
  onCreate: (title: string, body: string) => Promise<void>;
  onUpdate: (note: Note, title: string, body: string) => Promise<void>;
  onDelete: (note: Note) => Promise<void>;
}) {
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string>();

  const visible = useMemo(() => {
    const needle = fold(query.trim());
    if (!needle) return notes;
    // Every word must appear somewhere, so "courses lait" narrows down.
    const words = needle.split(/\s+/u);
    return notes.filter((note) => {
      const haystack = fold(`${note.title} ${note.body}`);
      return words.every((word) => haystack.includes(word));
    });
  }, [notes, query]);

  const closeComposer = () => {
    setComposing(false);
    setTitle("");
    setBody("");
    setError("");
  };

  const createNote = async () => {
    const cleanTitle = title.trim();
    const cleanBody = body.trim();
    if (!cleanTitle || !cleanBody) {
      setError("Ajoutez un titre et un contenu.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onCreate(cleanTitle, cleanBody);
      closeComposer();
    } catch {
      setError("La note n’a pas pu être enregistrée. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="notes-view" aria-labelledby="notes-title">
      <div className="section-heading">
        <div>
          <h1 id="notes-title">Mes notes</h1>
          <p>
            {notes.length === 0
              ? "Vos idées retrouvées, sans quitter ParleNote."
              : `${notes.length} note${notes.length > 1 ? "s" : ""} dans votre espace privé.`}
          </p>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={() => setComposing(true)}
          aria-label="Créer une note manuellement"
        >
          <Plus aria-hidden="true" />
        </button>
      </div>

      {activity.length > 0 && (
        <details className="activity-log">
          <summary>
            <ExternalLink size={16} aria-hidden="true" />
            Créé chez Google ({activity.length})
          </summary>
          <ul>
            {activity.map((entry) => (
              <li key={entry.id}>
                {entry.destination === "calendar" ? (
                  <CalendarDays size={16} aria-hidden="true" />
                ) : (
                  <FileText size={16} aria-hidden="true" />
                )}
                <a href={entry.url} target="_blank" rel="noreferrer">
                  {entry.title}
                </a>
                <time dateTime={entry.createdAt}>
                  {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" }).format(
                    new Date(entry.createdAt)
                  )}
                </time>
              </li>
            ))}
          </ul>
        </details>
      )}

      {notes.length > 3 && (
        <div className="notes-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher dans vos notes…"
            aria-label="Rechercher dans vos notes"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="Effacer la recherche">
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {composing && (
        <form
          className="note-composer"
          onSubmit={(event) => {
            event.preventDefault();
            void createNote();
          }}
        >
          <div className="note-composer-heading">
            <div>
              <strong>Nouvelle note</strong>
              <span>Écrivez directement, sans passer par l’assistant.</span>
            </div>
            <button type="button" className="close-button" onClick={closeComposer} aria-label="Fermer">
              <X aria-hidden="true" />
            </button>
          </div>
          <label htmlFor="manual-note-title">Titre</label>
          <input
            id="manual-note-title"
            value={title}
            maxLength={160}
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Titre de la note"
          />
          <label htmlFor="manual-note-body">Contenu</label>
          <textarea
            id="manual-note-body"
            value={body}
            maxLength={50_000}
            rows={7}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Écrivez votre note…"
          />
          {error && <p className="inline-error" role="alert">{error}</p>}
          <div className="editor-actions">
            <button className="button ghost" type="button" onClick={closeComposer}>
              Annuler
            </button>
            <button className="button primary" type="submit" disabled={saving}>
              <Check size={18} aria-hidden="true" />
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p role="status">Chargement des notes…</p>
      ) : query && visible.length === 0 ? (
        <p className="notes-empty-search" role="status">
          Aucune note ne contient «&nbsp;{query.trim()}&nbsp;».
        </p>
      ) : notes.length === 0 && !composing ? (
        <div className="empty-notes">
          <img src="/voice-to-note.jpg" alt="" width="768" height="512" loading="eager" />
          <h2>Votre prochaine idée commence ici</h2>
          <p>Parlez à l’assistant ou écrivez une note directement.</p>
          <div className="empty-note-actions">
            <button className="button primary" type="button" onClick={() => setComposing(true)}>
              <Plus size={18} aria-hidden="true" />
              Écrire une note
            </button>
            <button className="button ghost" type="button" onClick={onCapture}>
              Utiliser la voix
            </button>
          </div>
        </div>
      ) : (
        <ul className="notes-list">
          {visible.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              expanded={openId === note.id}
              onToggle={() => setOpenId(openId === note.id ? undefined : note.id)}
              onSave={(nextTitle, nextBody) => onUpdate(note, nextTitle, nextBody)}
              onDelete={async () => {
                await onDelete(note);
                setOpenId(undefined);
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
